import gsap from "gsap";
import {
	DOT_COLS,
	DOT_PROXIMITY_DURATION_S,
	DOT_PROXIMITY_ENABLE_BUFFER_MS,
	DOT_PROXIMITY_MAGNETIC_FALLOFF_POWER,
	DOT_PROXIMITY_MAGNETIC_HEIGHT_RATIO,
	DOT_PROXIMITY_MAGNETIC_PULL,
	DOT_PROXIMITY_MAGNETIC_WIDTH_RATIO,
	DOT_PROXIMITY_MAX_SCALE,
	DOT_PROXIMITY_RESET_DURATION_S,
	DOT_PROXIMITY_SCALE_FALLOFF_POWER,
	DOT_PROXIMITY_SCALE_HEIGHT_RATIO,
	DOT_PROXIMITY_SCALE_WIDTH_RATIO,
	DOT_SCATTER_CLIP_PAD_PX,
	GRID_ENTRANCE_DOTS_DURATION_S,
	GRID_ENTRANCE_DOTS_EASE,
} from "./timeline-constants";
import {
	getDotScatterMotion,
	getEllipseProximity,
	getEventTagKey,
	getScatterMinEndMs,
	getTimelineEventControlId,
	getTimelineEventPanelId,
	formatDictionaryTemplate,
	prefersReducedMotion,
	type DotScatterVariant,
	type TimelineDotItem,
} from "./timeline-utils";

export type YearDotsOptions = {
	dotItems: TimelineDotItem[];
	size?: "default" | "lg";
	layout?: "grid" | "row";
	activeIndex?: number;
	onDotClick?: (index: number) => void;
	scatterToken?: number;
	scatterBaseDelay?: number;
	scatterVariant?: DotScatterVariant;
	scatterDotStaggerS?: number;
	entranceToken?: number;
	proximityStage?: HTMLElement;
	getDotId?: (dotIndex: number) => number;
	onScatterComplete?: () => void;
	listClassName?: string;
	yearName?: string;
	dotCols?: number;
	eventLabel?: string;
	eventsInYear?: string;
};

function getDotAccessibilityLabel(dotItem: TimelineDotItem, eventLabel = "Event"): string {
	return dotItem.event.name ?? dotItem.event.description ?? dotItem.yearName ?? eventLabel;
}

function createDot(
	dotItem: TimelineDotItem,
	dotIndex: number,
	size: "default" | "lg",
	dotRefs: Map<number, HTMLElement>,
	getDotId: (dotIndex: number) => number,
	options?: {
		activeIndex?: number;
		onDotClick?: (index: number) => void;
		yearName?: string;
		eventLabel?: string;
	}
): HTMLElement {
	const tagKey = getEventTagKey(dotItem.event);
	const dotId = getDotId(dotIndex);
	const isInteractive = Boolean(options?.onDotClick);
	const dot = isInteractive
		? document.createElement("button")
		: document.createElement("span");

	if (isInteractive) {
		(dot as HTMLButtonElement).type = "button";
	}

	dot.className = `historical-timeline__dot historical-timeline__dot--${tagKey}${
		size === "lg" ? " historical-timeline__dot--lg" : ""
	}${options?.activeIndex === dotIndex ? " is-active" : ""}`;
	dot.dataset.dotId = String(dotId);

	if (isInteractive) {
		const label = getDotAccessibilityLabel(dotItem, options?.eventLabel);
		dot.setAttribute("aria-label", label);

		if (options?.activeIndex === dotIndex) {
			dot.setAttribute("aria-current", "true");
		}

		if (options?.yearName) {
			dot.id = getTimelineEventControlId(options.yearName, dotIndex);
			dot.setAttribute("aria-controls", getTimelineEventPanelId(options.yearName, dotIndex));
		}

		dot.addEventListener("click", (event) => {
			event.stopPropagation();
			options?.onDotClick?.(dotIndex);
		});
	} else {
		dot.setAttribute("aria-hidden", "true");
	}

	dotRefs.set(dotId, dot);

	return dot;
}

function wrapDotItem(dot: HTMLElement): HTMLLIElement {
	const item = document.createElement("li");
	item.className = "historical-timeline__dots-item";
	item.appendChild(dot);
	return item;
}

function createDotsList(isDecorative: boolean): HTMLUListElement {
	const list = document.createElement("ul");
	list.className = "historical-timeline__dots-list";

	if (isDecorative) {
		list.setAttribute("aria-hidden", "true");
	}

	return list;
}

function finalizeScatterTransforms(dotRefs: Map<number, HTMLElement>): void {
	dotRefs.forEach((element) => {
		gsap.killTweensOf(element);
		gsap.set(element, { clearProps: "transform,opacity,filter" });
	});
}

function runEntranceAnimation(host: HTMLElement): gsap.core.Tween | undefined {
	if (prefersReducedMotion()) {
		gsap.set(host, { scaleY: 1 });
		return undefined;
	}

	gsap.set(host, { scaleY: 0, transformOrigin: "bottom center" });
	return gsap.to(host, {
		scaleY: 1,
		duration: GRID_ENTRANCE_DOTS_DURATION_S,
		ease: GRID_ENTRANCE_DOTS_EASE,
		overwrite: "auto",
	});
}

function appendDotsToHost(
	host: HTMLElement,
	dotItems: TimelineDotItem[],
	size: "default" | "lg",
	dotRefs: Map<number, HTMLElement>,
	getDotId: (dotIndex: number) => number,
	entranceToken?: number,
	layout: "grid" | "row" = "grid",
	dotCols: number = DOT_COLS,
	dotOptions?: {
		activeIndex?: number;
		onDotClick?: (index: number) => void;
		listClassName?: string;
		yearName?: string;
		eventLabel?: string;
		eventsInYear?: string;
	}
): { entranceTween?: gsap.core.Tween; dotsList: HTMLUListElement } {
	const isInteractive = Boolean(dotOptions?.onDotClick);
	const dotFactory = (dotItem: TimelineDotItem, dotIndex: number) =>
		createDot(dotItem, dotIndex, size, dotRefs, getDotId, dotOptions);

	const list = createDotsList(!isInteractive);

	if (dotOptions?.listClassName) {
		list.classList.add(...dotOptions.listClassName.split(/\s+/).filter(Boolean));
	}

	if (isInteractive && dotOptions?.yearName) {
		list.setAttribute(
			"aria-label",
			formatDictionaryTemplate(dotOptions.eventsInYear ?? "Events in {0}", dotOptions.yearName)
		);
	}

	if (layout === "row") {
		list.classList.add("historical-timeline__dots-row", "historical-timeline__dots-row--horizontal");

		dotItems.forEach((dotItem, dotIndex) => {
			list.appendChild(wrapDotItem(dotFactory(dotItem, dotIndex)));
		});
		host.appendChild(list);

		return {
			dotsList: list,
			entranceTween: entranceToken ? runEntranceAnimation(list) : undefined,
		};
	}

	if (dotItems.length <= dotCols) {
		list.classList.add("historical-timeline__dots-row");
		if (entranceToken) {
			list.classList.add("historical-timeline__dots-entrance");
		}

		dotItems.forEach((dotItem, dotIndex) => {
			list.appendChild(wrapDotItem(dotFactory(dotItem, dotIndex)));
		});
		host.appendChild(list);

		return {
			dotsList: list,
			entranceTween: entranceToken ? runEntranceAnimation(list) : undefined,
		};
	}

	list.classList.add("historical-timeline__dots-stack");
	if (entranceToken) {
		list.classList.add("historical-timeline__dots-entrance");
	}

	for (let rowIndex = 0; rowIndex < dotItems.length; rowIndex += dotCols) {
		const rowItems = dotItems.slice(rowIndex, rowIndex + dotCols);
		const row = document.createElement("li");
		row.className = "historical-timeline__dots-row";

		rowItems.forEach((dotItem, colIndex) => {
			row.appendChild(dotFactory(dotItem, rowIndex + colIndex));
		});

		list.appendChild(row);
	}

	host.appendChild(list);

	return {
		dotsList: list,
		entranceTween: entranceToken ? runEntranceAnimation(list) : undefined,
	};
}

function runScatterAnimation(
	dotItems: TimelineDotItem[],
	dotRefs: Map<number, HTMLElement>,
	scatterToken: number,
	scatterBaseDelay: number,
	getDotId: (dotIndex: number) => number,
	scatterVariant: DotScatterVariant = "full",
	scatterDotStaggerS = 0
): gsap.core.Tween[] {
	const tweens: gsap.core.Tween[] = [];
	const useBlur = scatterVariant === "full";

	dotItems.forEach((_dotItem, dotIndex) => {
		const dotId = getDotId(dotIndex);
		const element = dotRefs.get(dotId);
		if (!element) {
			return;
		}

		const motion = getDotScatterMotion(dotId + scatterToken * 9973, scatterVariant);
		const delay = scatterBaseDelay + motion.delay + dotIndex * scatterDotStaggerS;
		const initialState: gsap.TweenVars = {
			x: motion.x,
			y: motion.y,
			scale: motion.scale,
			opacity: 0,
			transformOrigin: "center center",
			force3D: true,
		};

		if (useBlur) {
			initialState.filter = `blur(${motion.blur}px)`;
		}

		gsap.set(element, initialState);

		const targetState: gsap.TweenVars = {
			x: 0,
			y: 0,
			scale: 1,
			opacity: 1,
			duration: motion.duration,
			delay,
			ease: "power2.out",
			overwrite: "auto",
		};

		if (useBlur) {
			targetState.filter = "blur(0px)";
		}

		tweens.push(gsap.to(element, targetState));
	});

	return tweens;
}

function bindProximity(
	dotItems: TimelineDotItem[],
	dotRefs: Map<number, HTMLElement>,
	stage: HTMLElement,
	scatterToken: number,
	scatterBaseDelay: number,
	getDotId: (dotIndex: number) => number
): () => void {
	const enableDelayMs =
		getScatterMinEndMs(dotItems, scatterToken, scatterBaseDelay, getDotId) + DOT_PROXIMITY_ENABLE_BUFFER_MS;

	let proximityActive = false;
	const restCenters = new Map<number, { x: number; y: number }>();

	const captureRestCenters = () => {
		restCenters.clear();

		dotItems.forEach((_dotItem, dotIndex) => {
			const dotId = getDotId(dotIndex);
			const element = dotRefs.get(dotId);
			if (!element) {
				return;
			}

			const rect = element.getBoundingClientRect();
			restCenters.set(dotId, {
				x: rect.left + rect.width / 2,
				y: rect.top + rect.height / 2,
			});
		});
	};

	const enableTimer = window.setTimeout(() => {
		requestAnimationFrame(() => {
			captureRestCenters();
			proximityActive = true;
		});
	}, enableDelayMs);

	const updateDotProximity = (clientX: number, clientY: number) => {
		if (!proximityActive) {
			return;
		}

		const stageRect = stage.getBoundingClientRect();

		dotItems.forEach((_dotItem, dotIndex) => {
			const dotId = getDotId(dotIndex);
			const element = dotRefs.get(dotId);
			const restCenter = restCenters.get(dotId);
			if (!element || !restCenter) {
				return;
			}

			const magneticProximity = getEllipseProximity(
				clientX,
				clientY,
				restCenter.x,
				restCenter.y,
				stageRect,
				DOT_PROXIMITY_MAGNETIC_WIDTH_RATIO,
				DOT_PROXIMITY_MAGNETIC_HEIGHT_RATIO,
				DOT_PROXIMITY_MAGNETIC_FALLOFF_POWER
			);

			const scaleProximity = getEllipseProximity(
				clientX,
				clientY,
				restCenter.x,
				restCenter.y,
				stageRect,
				DOT_PROXIMITY_SCALE_WIDTH_RATIO,
				DOT_PROXIMITY_SCALE_HEIGHT_RATIO,
				DOT_PROXIMITY_SCALE_FALLOFF_POWER
			);
			const pull = magneticProximity * DOT_PROXIMITY_MAGNETIC_PULL;

			gsap.to(element, {
				x: (restCenter.x - clientX) * pull,
				y: (restCenter.y - clientY) * pull,
				scale: 1 + (DOT_PROXIMITY_MAX_SCALE - 1) * scaleProximity,
				duration: DOT_PROXIMITY_DURATION_S,
				ease: "power2.out",
				overwrite: "auto",
			});
		});
	};

	const handleMouseMove = (event: MouseEvent) => {
		updateDotProximity(event.clientX, event.clientY);
	};

	const handleMouseLeave = () => {
		if (!proximityActive) {
			return;
		}

		dotItems.forEach((_dotItem, dotIndex) => {
			const dotId = getDotId(dotIndex);
			const element = dotRefs.get(dotId);
			if (!element) {
				return;
			}

			gsap.to(element, {
				x: 0,
				y: 0,
				scale: 1,
				duration: DOT_PROXIMITY_RESET_DURATION_S,
				ease: "power2.out",
				overwrite: "auto",
			});
		});
	};

	stage.addEventListener("mousemove", handleMouseMove);
	stage.addEventListener("mouseleave", handleMouseLeave);

	return () => {
		window.clearTimeout(enableTimer);
		stage.removeEventListener("mousemove", handleMouseMove);
		stage.removeEventListener("mouseleave", handleMouseLeave);
	};
}

export function createYearDots(options: YearDotsOptions): {
	element: HTMLElement;
	dotsList?: HTMLUListElement;
	cleanup: () => void;
} {
	const {
		dotItems,
		size = "default",
		layout = "grid",
		activeIndex,
		onDotClick,
		scatterToken,
		scatterBaseDelay = 0,
		scatterVariant = "full",
		scatterDotStaggerS = 0,
		entranceToken,
		proximityStage,
		getDotId = (dotIndex) => dotIndex,
		onScatterComplete,
		listClassName,
		yearName,
		dotCols = DOT_COLS,
		eventLabel = "Event",
		eventsInYear = "Events in {0}",
	} = options;

	const root = document.createElement("div");
	root.className = layout === "row"
		? "historical-timeline__dots historical-timeline__dots--row"
		: "historical-timeline__dots";

	const dotRefs = new Map<number, HTMLElement>();
	const cleanups: Array<() => void> = [];
	let scatterTweens: gsap.core.Tween[] = [];

	if (dotItems.length === 0) {
		root.hidden = true;
		return { element: root, cleanup: () => undefined };
	}

	let dotsHost: HTMLElement = root;

	if (scatterToken !== undefined) {
		const scatterClip = document.createElement("div");
		scatterClip.className = "historical-timeline__dots-scatter-clip";
		scatterClip.style.padding = `${DOT_SCATTER_CLIP_PAD_PX}px`;
		scatterClip.style.margin = `-${DOT_SCATTER_CLIP_PAD_PX}px`;
		root.appendChild(scatterClip);
		dotsHost = scatterClip;
	}

	const { entranceTween, dotsList } = appendDotsToHost(
		dotsHost,
		dotItems,
		size,
		dotRefs,
		getDotId,
		entranceToken && scatterToken === undefined ? entranceToken : undefined,
		layout,
		dotCols,
		layout === "row" ? { activeIndex, onDotClick, listClassName, yearName, eventLabel, eventsInYear } : undefined
	);

	const scheduleScatterComplete = () => {
		if (!onScatterComplete) {
			return;
		}

		queueMicrotask(onScatterComplete);
	};

	if (scatterToken !== undefined && !prefersReducedMotion()) {
		scatterTweens = runScatterAnimation(
			dotItems,
			dotRefs,
			scatterToken,
			scatterBaseDelay,
			getDotId,
			scatterVariant,
			scatterDotStaggerS
		);

		if (proximityStage) {
			cleanups.push(
				bindProximity(dotItems, dotRefs, proximityStage, scatterToken, scatterBaseDelay, getDotId)
			);
		}

		if (onScatterComplete) {
			const endMs = getScatterMinEndMs(dotItems, scatterToken, scatterBaseDelay, getDotId);
			const scatterCompleteTimer = window.setTimeout(() => {
				finalizeScatterTransforms(dotRefs);
				onScatterComplete();
			}, endMs);
			cleanups.push(() => window.clearTimeout(scatterCompleteTimer));
		}
	} else {
		finalizeScatterTransforms(dotRefs);
		scheduleScatterComplete();
	}

	return {
		element: root,
		dotsList,
		cleanup: () => {
			entranceTween?.kill();
			scatterTweens.forEach((tween) => tween.kill());
			cleanups.forEach((cleanup) => cleanup());
			dotRefs.forEach((element) => gsap.set(element, { clearProps: "transform,opacity,filter" }));
		},
	};
}
